import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../components/customAxios';
import baseUrl from '../../base_url';
import StatusBadge from '../../components/common/StatusBadge';
import {
  ArrowLeft,
  ShoppingBag,
  CreditCard,
  Truck,
  User,
  Package,
  Clock,
  AlertCircle,
  ExternalLink,
  Download,
  RefreshCw,
  Copy,
  Printer
} from 'lucide-react';
import toast from 'react-hot-toast';

export const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals & Action loading states
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isCreatingShipment, setIsCreatingShipment] = useState(false);
  const [isSyncingStatus, setIsSyncingStatus] = useState(false);
  const [isSyncingPayment, setIsSyncingPayment] = useState(false);
  const [isDownloadingLabel, setIsDownloadingLabel] = useState(false);

  // Tracking Modal State
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [trackingInput, setTrackingInput] = useState('');
  const [isSubmittingTracking, setIsSubmittingTracking] = useState(false);

  // Manual Mark Paid Modal State
  const [isMarkPaidModalOpen, setIsMarkPaidModalOpen] = useState(false);
  const [markPaidNote, setMarkPaidNote] = useState('');
  const [markPaidRef, setMarkPaidRef] = useState('');
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    try {
      const res = await axios.get(`${baseUrl}api/admin/get-order/${id}`);
      if (res.data?.data) {
        setOrder(res.data.data);
        if (res.data.data.tracking) {
          setTrackingInput(res.data.data.tracking);
        } else if (res.data.data.shipping?.trackingNumber) {
          setTrackingInput(res.data.data.shipping.trackingNumber);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch order');
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  // Status transitions state machine
  const LEGAL_TRANSITIONS = {
    pending: ['placed', 'cancelled'],
    placed: ['confirmed', 'processing', 'cancelled'],
    confirmed: ['processing', 'shipped', 'cancelled'],
    processing: ['shipped', 'cancelled'],
    shipped: ['out_for_delivery', 'delivered', 'cancelled'],
    out_for_delivery: ['delivered', 'cancelled'],
    delivered: ['completed', 'refunded'],
    completed: ['refunded'],
    cancelled: ['refunded'],
    refunded: []
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!order || newStatus === order.status) return;
    setIsUpdatingStatus(true);
    try {
      await axios.post(`${baseUrl}api/admin/edit-status/${id}`, { newStatus });
      toast.success(`Order status updated to '${newStatus}'`);
      fetchOrderDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update order status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Add Tracking
  const handleSubmitTracking = async (e) => {
    e.preventDefault();
    if (!trackingInput.trim()) return;
    setIsSubmittingTracking(true);
    try {
      await axios.post(`${baseUrl}api/admin/add-tracking/${id}`, { tracking: trackingInput.trim() });
      toast.success('Tracking number added successfully');
      setIsTrackingModalOpen(false);
      fetchOrderDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save tracking');
    } finally {
      setIsSubmittingTracking(false);
    }
  };

  // Create Sendcloud Shipment
  const handleCreateSendCloudShipment = async () => {
    setIsCreatingShipment(true);
    try {
      await axios.post(`${baseUrl}api/admin/orders/${id}/sendcloud/create-shipment`);
      toast.success('Sendcloud shipment created successfully!');
      fetchOrderDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create Sendcloud shipment');
    } finally {
      setIsCreatingShipment(false);
    }
  };

  // Sync Sendcloud Status
  const handleSyncSendcloudStatus = async () => {
    setIsSyncingStatus(true);
    try {
      await axios.post(`${baseUrl}api/admin/orders/${id}/sendcloud/sync-status`);
      toast.success('Sendcloud tracking synced!');
      fetchOrderDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to sync status');
    } finally {
      setIsSyncingStatus(false);
    }
  };

  // Sync Payment from Stripe / PayPal
  const handleSyncPayment = async () => {
    setIsSyncingPayment(true);
    try {
      const res = await axios.post(`${baseUrl}api/admin/orders/${id}/sync-payment`);
      toast.success(res.data?.message || 'Payment verified with gateway');
      fetchOrderDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to sync payment');
    } finally {
      setIsSyncingPayment(false);
    }
  };

  // Manual Mark Paid
  const handleManualMarkPaid = async (e) => {
    e.preventDefault();
    setIsMarkingPaid(true);
    try {
      await axios.post(`${baseUrl}api/admin/orders/${id}/manual-mark-paid`, {
        paymentId: markPaidRef.trim() || undefined,
        note: markPaidNote.trim() || undefined
      });
      toast.success('Order marked as Paid');
      setIsMarkPaidModalOpen(false);
      fetchOrderDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark paid');
    } finally {
      setIsMarkingPaid(false);
    }
  };

  // Download Label PDF
  const handleDownloadLabel = async () => {
    setIsDownloadingLabel(true);
    try {
      const res = await axios.get(`${baseUrl}api/admin/orders/${id}/sendcloud/label`, {
        responseType: 'blob'
      });
      const file = new Blob([res.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } catch (err) {
      toast.error('Shipping label not ready or unavailable');
    } finally {
      setIsDownloadingLabel(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${label} to clipboard`);
  };

  if (loading) {
    return (
      <div className="p-16 text-center text-stone-400">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-stone-400" />
        <p className="text-sm font-medium text-stone-600">Loading order workspace...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-12 bg-white rounded-xl border border-stone-200 text-center max-w-lg mx-auto mt-10">
        <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
        <h3 className="font-bold text-stone-900 text-base">Order Not Found</h3>
        <p className="text-xs text-stone-500 mt-1">{error || 'This order does not exist or was deleted.'}</p>
        <button
          onClick={() => navigate('/orders')}
          className="mt-4 px-4 py-2 bg-stone-900 text-white rounded-lg text-xs font-semibold"
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const currentStatus = (order.status || 'pending').toLowerCase();
  const allowedNextStatuses = LEGAL_TRANSITIONS[currentStatus] || [];
  const pricing = order.pricing || {};
  const totalAmount = Number(pricing.totalAmount || order.totalAmount || 0);
  const subtotal = Number(pricing.subtotal || totalAmount);
  const shippingCost = Number(pricing.shippingCost || 0);
  const discount = Number(pricing.discount || 0);
  const tax = Number(pricing.tax || 0);

  const address = order.shippingAddress || (Array.isArray(order.address) ? order.address[0] : {}) || {};
  const trackingNumber = order.tracking || order.shipping?.trackingNumber;
  const trackingUrl = order.shipping?.trackingUrl;
  const parcelId = order.shipping?.sendcloudParcelId;

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Top Breadcrumb & Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <button
            onClick={() => navigate('/orders')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-500 hover:text-stone-900 mb-2 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Orders
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-stone-900 font-mono">
              {order.orderNumber || order._id}
            </h1>
            <StatusBadge type="order" status={order.status} />
            <StatusBadge
              type="order"
              status={
                order.payment?.status === 'completed'
                  ? 'paid'
                  : order.payment?.status === 'failed'
                  ? 'failed'
                  : 'pending'
              }
            />
          </div>
          <p className="text-xs text-stone-400 mt-1">
            Placed on {new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-xs font-semibold transition-colors"
          >
            <Printer className="w-3.5 h-3.5" />
            Print Order
          </button>

          {/* Sync Payment */}
          <button
            onClick={handleSyncPayment}
            disabled={isSyncingPayment}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isSyncingPayment ? 'animate-spin' : ''}`} />
            Sync Gateway
          </button>

          {/* Status Progression Dropdown */}
          {allowedNextStatuses.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) handleUpdateStatus(e.target.value);
              }}
              disabled={isUpdatingStatus}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold px-3 py-1.5 cursor-pointer focus:outline-none"
            >
              <option value="" disabled>Advance Status →</option>
              {allowedNextStatuses.map(st => (
                <option key={st} value={st} className="text-stone-900 bg-white">
                  Mark as {st.charAt(0).toUpperCase() + st.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Main 2-Column Operations Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN: Items, Financials, Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-stone-400" />
                Line Items ({order.cart?.length || 0})
              </h2>
            </div>

            <div className="divide-y divide-stone-100">
              {Array.isArray(order.cart) && order.cart.map((item, idx) => {
                const itemPrice = Number(item.price || 0);
                const itemQty = Number(item.quantity || 1);
                const lineTotal = itemPrice * itemQty;

                return (
                  <div key={item._id || idx} className="py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-12 h-12 rounded-lg object-cover border border-stone-200"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center text-stone-400">
                          <Package className="w-5 h-5" />
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-stone-900 text-xs">{item.title}</div>
                        <div className="text-[11px] text-stone-400 font-mono mt-0.5">
                          SKU: {item.articleCode || 'N/A'} • Qty: {itemQty}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="font-bold text-stone-900 text-xs">£{lineTotal.toFixed(2)}</div>
                      <div className="text-[10px] text-stone-400">£{itemPrice.toFixed(2)} each</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Financial Breakdown */}
          <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm">
            <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-2 mb-4">
              <CreditCard className="w-4 h-4 text-stone-400" />
              Financial Breakdown
            </h2>

            <div className="space-y-2 text-xs divide-y divide-stone-100">
              <div className="flex justify-between py-1 text-stone-600">
                <span>Subtotal:</span>
                <span className="font-medium text-stone-900">£{subtotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between py-1 text-emerald-700">
                  <span>Coupon Discount ({pricing.couponCode || 'PROMO'}):</span>
                  <span className="font-medium">-£{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between py-1 text-stone-600">
                <span>Shipping Fee:</span>
                <span className="font-medium text-stone-900">£{shippingCost.toFixed(2)}</span>
              </div>
              {tax > 0 && (
                <div className="flex justify-between py-1 text-stone-600">
                  <span>Estimated Tax:</span>
                  <span className="font-medium text-stone-900">£{tax.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between py-2 text-sm font-bold text-stone-900 border-t border-stone-200">
                <span>Total Amount Charged:</span>
                <span className="font-mono text-base">£{totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Gateway & Payment Details */}
            <div className="mt-4 pt-3 border-t border-stone-100 grid grid-cols-2 gap-4 text-xs bg-stone-50 p-3 rounded-lg">
              <div>
                <span className="text-stone-400 block text-[10px] uppercase font-bold">Payment Method</span>
                <span className="font-semibold text-stone-800 capitalize">
                  {order.payment?.method || (order.paymentMethod ? order.paymentMethod[0] : 'Card')}
                </span>
              </div>
              <div>
                <span className="text-stone-400 block text-[10px] uppercase font-bold">Transaction Reference</span>
                <span className="font-mono text-stone-800 truncate block">
                  {order.paymentId || order.stripeSessionId || order.paypalOrderId || order.payment?.transactionId || 'None'}
                </span>
              </div>
            </div>
          </div>

          {/* Chronological Event Timeline */}
          <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm">
            <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-stone-400" />
              Order Lifecycle Timeline
            </h2>

            <div className="relative pl-6 space-y-6 border-l-2 border-stone-200 text-xs">
              {/* Event 1: Placed */}
              <div className="relative">
                <span className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" />
                <div className="font-semibold text-stone-900">Order Placed</div>
                <div className="text-stone-400 text-[11px]">{new Date(order.placedAt || order.createdAt).toLocaleString()}</div>
              </div>

              {/* Event 2: Payment */}
              <div className="relative">
                <span className={`absolute -left-[31px] top-0.5 w-4 h-4 rounded-full border-2 border-white ${order.payment?.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                <div className="font-semibold text-stone-900">
                  Payment {order.payment?.status === 'completed' ? 'Confirmed' : 'Pending'}
                </div>
                <div className="text-stone-400 text-[11px]">
                  Method: {order.payment?.method || 'Card'} {order.paymentDate ? `• ${new Date(order.paymentDate).toLocaleString()}` : ''}
                </div>
              </div>

              {/* Event 3: Shipment Created */}
              {parcelId && (
                <div className="relative">
                  <span className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-sky-500 border-2 border-white" />
                  <div className="font-semibold text-stone-900">Sendcloud Parcel Created</div>
                  <div className="text-stone-400 text-[11px]">Parcel ID: {parcelId}</div>
                </div>
              )}

              {/* Event 4: Shipped */}
              {trackingNumber && (
                <div className="relative">
                  <span className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-sky-600 border-2 border-white" />
                  <div className="font-semibold text-stone-900">Dispatched & Tracking Attached</div>
                  <div className="text-stone-400 text-[11px]">Code: {trackingNumber}</div>
                </div>
              )}

              {/* Event 5: Completed */}
              {(currentStatus === 'delivered' || currentStatus === 'completed') && (
                <div className="relative">
                  <span className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full bg-emerald-600 border-2 border-white" />
                  <div className="font-semibold text-stone-900">Delivered & Completed</div>
                  <div className="text-stone-400 text-[11px]">Lifecycle finalized</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Customer, Fulfillment, Actions */}
        <div className="space-y-6">
          {/* Customer Details Card */}
          <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm">
            <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-stone-400" />
              Customer & Shipping Address
            </h2>

            <div className="space-y-3 text-xs">
              <div>
                <div className="font-bold text-stone-900">{address.fullName || 'Customer'}</div>
                <div className="text-stone-500">{order.email}</div>
                <div className="text-stone-500">{address.phone || 'No phone provided'}</div>
              </div>

              <div className="pt-2 border-t border-stone-100">
                <div className="text-stone-400 text-[10px] font-bold uppercase mb-1">Delivery Address</div>
                <div className="text-stone-700 leading-relaxed">
                  {address.houseNumber && `${address.houseNumber}, `}{address.streetAddress}<br />
                  {address.city}, {address.postalCode}<br />
                  {address.country || 'United Kingdom'}
                </div>
                <button
                  onClick={() => copyToClipboard(`${address.fullName || ''}\n${address.streetAddress || ''}\n${address.city || ''} ${address.postalCode || ''}`, 'Address')}
                  className="mt-2 text-[11px] text-amber-700 hover:underline flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" /> Copy Address
                </button>
              </div>
            </div>
          </div>

          {/* Sendcloud Fulfillment Workspace Card */}
          <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm space-y-4">
            <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
              <Truck className="w-4 h-4 text-stone-400" />
              Sendcloud Logistics
            </h2>

            {/* Tracking / Parcel Info */}
            <div className="bg-stone-50 p-3 rounded-lg text-xs space-y-1.5 border border-stone-100">
              <div className="flex justify-between">
                <span className="text-stone-400">Carrier:</span>
                <span className="font-medium text-stone-800">{order.shipping?.courierName || 'Royal Mail / Sendcloud'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-400">Parcel ID:</span>
                <span className="font-mono text-stone-800">{parcelId || 'Not Created'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-400">Tracking:</span>
                <span className="font-mono text-stone-800">{trackingNumber || 'Pending'}</span>
              </div>
            </div>

            {/* Actions for Sendcloud */}
            <div className="space-y-2 pt-1">
              {!parcelId ? (
                <button
                  onClick={handleCreateSendCloudShipment}
                  disabled={isCreatingShipment}
                  className="w-full flex items-center justify-center gap-2 py-2 bg-stone-900 hover:bg-stone-800 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors disabled:opacity-50"
                >
                  <Truck className="w-3.5 h-3.5" />
                  {isCreatingShipment ? 'Generating Parcel...' : 'Create Sendcloud Shipment'}
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleDownloadLabel}
                    disabled={isDownloadingLabel}
                    className="flex items-center justify-center gap-1.5 py-2 bg-stone-100 hover:bg-stone-200 text-stone-800 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Label PDF
                  </button>
                  <button
                    onClick={handleSyncSendcloudStatus}
                    disabled={isSyncingStatus}
                    className="flex items-center justify-center gap-1.5 py-2 bg-stone-100 hover:bg-stone-200 text-stone-800 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isSyncingStatus ? 'animate-spin' : ''}`} />
                    Sync Status
                  </button>
                </div>
              )}

              {trackingUrl && (
                <a
                  href={trackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-center justify-center gap-1.5 py-2 border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 rounded-lg text-xs font-semibold transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open Live Tracking Page
                </a>
              )}
            </div>
          </div>

          {/* Manual Operational Actions */}
          <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm space-y-3">
            <h2 className="text-sm font-semibold text-stone-900">Manual Interventions</h2>
            <div className="space-y-2">
              <button
                onClick={() => setIsTrackingModalOpen(true)}
                className="w-full text-left px-3 py-2 rounded-lg bg-stone-50 hover:bg-stone-100 text-stone-800 text-xs font-semibold border border-stone-200 transition-colors flex items-center justify-between"
              >
                <span>Add / Edit Manual Tracking</span>
                <span className="text-stone-400">→</span>
              </button>

              {order.payment?.status !== 'completed' && (
                <button
                  onClick={() => setIsMarkPaidModalOpen(true)}
                  className="w-full text-left px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 transition-colors flex items-center justify-between"
                >
                  <span>Manual Mark as Paid</span>
                  <span className="text-amber-600">→</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Manual Tracking Modal */}
      {isTrackingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-6 max-w-md w-full">
            <h3 className="font-bold text-stone-900 text-sm mb-1">Add or Update Tracking Code</h3>
            <p className="text-xs text-stone-500 mb-4">
              Enter courier tracking code or URL for customer order tracking.
            </p>
            <form onSubmit={handleSubmitTracking} className="space-y-4">
              <input
                type="text"
                value={trackingInput}
                onChange={(e) => setTrackingInput(e.target.value)}
                placeholder="e.g. GB123456789 or https://track.sendcloud.sc/..."
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-900 focus:outline-none focus:border-stone-400"
                required
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsTrackingModalOpen(false)}
                  className="px-3 py-1.5 bg-stone-100 text-stone-700 rounded-lg text-xs font-semibold hover:bg-stone-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingTracking}
                  className="px-4 py-1.5 bg-stone-900 text-white rounded-lg text-xs font-semibold hover:bg-stone-800 disabled:opacity-50"
                >
                  {isSubmittingTracking ? 'Saving...' : 'Save Tracking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Mark Paid Modal */}
      {isMarkPaidModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-6 max-w-md w-full">
            <h3 className="font-bold text-stone-900 text-sm mb-1">Manual Mark as Paid</h3>
            <p className="text-xs text-stone-500 mb-4">
              Authorizes order status progression for bank transfers or offline payments. An audit event will be recorded.
            </p>
            <form onSubmit={handleManualMarkPaid} className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-stone-600 mb-1">Transaction Reference (Optional)</label>
                <input
                  type="text"
                  value={markPaidRef}
                  onChange={(e) => setMarkPaidRef(e.target.value)}
                  placeholder="e.g. BANK-TXN-98421"
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-900 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-stone-600 mb-1">Operational Note</label>
                <textarea
                  value={markPaidNote}
                  onChange={(e) => setMarkPaidNote(e.target.value)}
                  placeholder="Reason for manual mark (e.g. verified on Barclays online banking)"
                  rows={2}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-900 focus:outline-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsMarkPaidModalOpen(false)}
                  className="px-3 py-1.5 bg-stone-100 text-stone-700 rounded-lg text-xs font-semibold hover:bg-stone-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isMarkingPaid}
                  className="px-4 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
                >
                  {isMarkingPaid ? 'Confirming...' : 'Confirm Paid'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;